function toggleNearForm(form, id, show) {
  if (!id || !form) return;
  const root = form.parentElement || document;
  const el = root.querySelector('#' + id);
  if (!el) return;

  el.classList.toggle('is-hidden', !show);
}

let recaptchaLoader;

function loadRecaptcha() {
  recaptchaLoader ??= new Promise(resolve => {
    window.pangolinRecaptchaReady = resolve;
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit&onload=pangolinRecaptchaReady';
    script.async = true;
    document.head.append(script);
  });
  return recaptchaLoader;
}

async function renderRecaptcha(form) {
  const el = form.querySelector('.g-recaptcha');
  if (!el || 'widgetId' in el.dataset) return;
  el.dataset.widgetId = '';
  await loadRecaptcha();
  el.dataset.widgetId = grecaptcha.render(el, { sitekey: el.dataset.sitekey });
}

const recaptchaObserver = new IntersectionObserver(entries => {
  entries.forEach(entry => {
    if (!entry.isIntersecting) return;
    recaptchaObserver.unobserve(entry.target);
    renderRecaptcha(entry.target.closest('.js_form'));
  });
}, { rootMargin: '200px' });

function fieldValue(form, name) {
  const field = form.elements[name];
  if (!field) return '';
  const first = field instanceof RadioNodeList ? field[0] : field;
  if (first.matches(':disabled')) return '';
  if (first.type === 'checkbox') return first.checked ? first.value : '';
  return field.value;
}

function syncConditions(form) {
  form.querySelectorAll('fieldset[data-show-if]').forEach(group => {
    const [name, values] = group.dataset.showIf.split('=');
    const show = values.split('|').includes(fieldValue(form, name));
    group.hidden = !show;
    group.disabled = !show;
  });
}

function initConditions(form) {
  if (!form.querySelector('[data-show-if]')) return;
  form.addEventListener('change', () => syncConditions(form));
  form.addEventListener('reset', () => setTimeout(() => syncConditions(form)));
  syncConditions(form);
}

function initSteps(form) {
  const steps = [...form.querySelectorAll('fieldset[data-step]')];
  if (!steps.length) return;
  let current = 0;

  const show = (index, focus) => {
    current = index;
    steps.forEach((step, i) => step.hidden = i !== index);
    if (focus) steps[index].querySelector('[data-step-title]')?.focus();
  };

  const next = () => {
    const invalid = [...steps[current].elements].find(field => !field.checkValidity());
    if (invalid) return invalid.reportValidity();
    show(current + 1, true);
  };

  form.addEventListener('click', event => {
    if (event.target.closest('[data-step-next]')) next();
    else if (event.target.closest('[data-step-back]')) show(current - 1, true);
  });

  form.addEventListener('keydown', event => {
    if (event.key !== 'Enter' || current === steps.length - 1 || !event.target.matches('input:not([type="checkbox"], [type="radio"])')) return;
    event.preventDefault();
    next();
  });

  form.addEventListener('reset', () => show(0));
  show(0);
}

const readyForms = new WeakSet();

function initForm(form) {
  if (readyForms.has(form)) return;
  readyForms.add(form);
  initConditions(form);
  initSteps(form);
  const submitBtn = form.querySelector('[type="submit"]');
  if (!submitBtn) return;

  const {
    successId,
    errorId,
    downloadUrl,
    formname: formName,
    conversion: conversionID
  } = form.dataset;

  const captcha = form.querySelector('.g-recaptcha');
  const originalText =
    'textContent' in submitBtn
      ? submitBtn.textContent
      : submitBtn.value || 'Send';

  if (captcha) {
    recaptchaObserver.observe(captcha);
    form.addEventListener('focusin', () => renderRecaptcha(form), { once: true });
    form.addEventListener('pointerdown', () => renderRecaptcha(form), { once: true });
  }

  form.closest('dialog')?.addEventListener('close', () => {
    if (!form.classList.contains('is-hidden')) return;
    form.classList.remove('is-hidden');
    form.parentElement?.querySelectorAll('[data-hide-on-submit]').forEach(el => el.classList.remove('is-hidden'));
    toggleNearForm(form, successId, false);
    submitBtn.disabled = false;
    if ('textContent' in submitBtn) submitBtn.textContent = originalText;
    else submitBtn.value = originalText;
  });

  form.addEventListener('submit', async function (event) {
    event.preventDefault();

    if (captcha && (!captcha.dataset.widgetId || !grecaptcha.getResponse(captcha.dataset.widgetId))) {
      renderRecaptcha(form);
      alert('Please complete the reCAPTCHA.');
      return;
    }
    const data = new FormData(form);

    submitBtn.disabled = true;
    if ('textContent' in submitBtn) submitBtn.textContent = 'Sending...';
    else submitBtn.value = 'Sending...';

    toggleNearForm(form, errorId, false);

    try {
      const response = await fetch(form.action, {
        method: form.method,
        headers: {
          'accept': 'application/json'
        },
        body: data
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      if (formName) {
        plausible('Form Submission', {
          props: {
            form_name: formName
          }
        });
      }

      if (typeof gtag === 'function') {
        if (formName) {
          gtag('event', 'generate_lead', {
            page_location: location.href,
            transport_type: 'beacon',
            form_name: formName
          });
        }
        if (conversionID) {
          gtag('event', 'conversion', {
            send_to: conversionID,
            transport_type: 'beacon',
            'value': 1.0,
            'currency': 'USD'
          });
        }
      }

      if (typeof fbq === 'function') {
        if (formName) {
          fbq('track', 'Lead', {
            form_name: formName
          });
        }
      }

      form.classList.add('is-hidden');
      form.parentElement?.querySelectorAll('[data-hide-on-submit]').forEach(el => el.classList.add('is-hidden'));
      toggleNearForm(form, successId, true);

      form.reset();
      if (captcha) grecaptcha.reset(captcha.dataset.widgetId);

      if ('textContent' in submitBtn) submitBtn.textContent = 'Sent!';
      else submitBtn.value = 'Sent!';

      if (downloadUrl) {
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = '';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }

    } catch (err) {
      console.error('Submission error:', err);

      toggleNearForm(form, errorId, true);

      submitBtn.disabled = false;
      if ('textContent' in submitBtn) submitBtn.textContent = originalText;
      else submitBtn.value = originalText;
    }
  });
}

document.addEventListener('DOMContentLoaded', () => document.querySelectorAll('.js_form').forEach(initForm));
document.addEventListener('js-form:added', event => event.target.matches('.js_form') && initForm(event.target));
